import Iconify from "@/components/ui/Iconify";
import { useTranslations } from "@/lib/static-intl";
import { useEffect, useRef, useState } from "react";
import PlacesSearchSuggestion from "./places-search-suggestion";
import { useSearchParams } from "next/navigation";
import { usePlaceViewNavigation } from "@/hooks/usePlaceViewNavigation";

const PlacesListSearchBar = ({
  addQueryParam,
  removeQueryParam,
  currentUserLocation,
  searchQuery,
  setSearchQuery,
  switchScreenToInitial,
  handleSearchAroundMyLocation,
  completePlacesReset,
  floating = false,
}) => {
  const searchParams = useSearchParams();
  const t = useTranslations("places");
  const searchInputRef = useRef(null);
  const searchFieldRef = useRef(null);
  const { routeToPlaceDetail } = usePlaceViewNavigation();

  const [searchInp, setSearchInp] = useState(searchQuery);
  const [isSearchInpInFocus, setIsSearchInpInFocus] = useState(false);

  const triggerPlaceSearch = (forcedSearch) => {
    const _query = forcedSearch || searchInp;
    if (_query && _query?.length > 0) {
      setSearchQuery(_query);
      addQueryParam("search-place", _query);
      setIsSearchInpInFocus(false);
    }
  };

  const triggerPlaceDetailsModal = (place) => {
    if (place) {
      const nextSearch = searchInp?.trim();
      addQueryParam("search-place", nextSearch || null);
      setSearchQuery(nextSearch || "");
      routeToPlaceDetail(place);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    triggerPlaceSearch();
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    setSearchInp("");
    setSearchQuery("");
    removeQueryParam("show-around-me");
    removeQueryParam("search-place");
    completePlacesReset();
  };

  useEffect(() => {
    if (!isSearchInpInFocus || typeof document === "undefined") return;

    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      body.style.overflow = previous.overflow;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, [isSearchInpInFocus]);

  const closeSearchSuggestions = () => {
    setIsSearchInpInFocus(false);
    searchFieldRef.current?.blur();
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setIsSearchInpInFocus(false);
      }
    };

    if (isSearchInpInFocus)
      document.addEventListener("mousedown", handleOutsideClick);
    else document.removeEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSearchInpInFocus]);
  return (
    <div
      ref={searchInputRef}
      className={
        floating
          ? "w-full flex items-center gap-2"
          : "w-full bg-white dark:bg-custom_black px-4 py-4 border-b dark:border-b-brown sticky top-0 left-0 z-[35] flex items-center gap-2"
      }
    >
      <form
        onSubmit={handleSearchSubmit}
        onReset={handleSearchReset}
        className={
          floating
            ? "w-full bg-white dark:bg-custom_black rounded-full shadow-[0_4px_14px_rgba(0,0,0,0.14)] relative flex items-center pl-3 pr-2 h-12"
            : "w-full bg-black/[0.05] dark:bg-dark_bg_grey rounded-full relative flex items-center pl-1 pr-2 h-11"
        }
      >
        {!floating && (
          <button
            type="button"
            onClick={() => {
              removeQueryParam("show-around-me");
              removeQueryParam("search-place");
              setSearchQuery("");
              switchScreenToInitial();
            }}
          >
            <Iconify
              icon={"solar:arrow-left-linear"}
              className={
                "block md:hidden !text-black/50 dark:!text-white/50 ml-2"
              }
            />
          </button>
        )}
        {searchParams.get("show-around-me") === "yes" && (
          <Iconify
            icon={"tdesign:location-filled"}
            className={"!text-black/55 dark:!text-white/60 ml-2 -mr-1"}
          />
        )}
        {searchParams.get("show-around-me") === "yes" ? (
          <input
            type="text"
            required
            autoComplete="off"
            className="outline-none bg-transparent w-full py-2 px-3 text-sm caret-red placeholder:font-semibold placeholder:tracking-tight"
            value={"My Location"}
            readOnly
          />
        ) : (
          <input
            type="text"
            required
            name="place-search"
            ref={searchFieldRef}
            placeholder={t("place search inp placeholder")}
            autoComplete="off"
            className="outline-none bg-transparent w-full py-2 px-3 text-sm caret-red placeholder:font-semibold placeholder:tracking-tight"
            value={searchInp}
            onChange={(e) => setSearchInp(e.target.value)}
            onFocus={() => setIsSearchInpInFocus(true)}
          />
        )}
        <div className="w-fit flex items-center justify-center gap-1">
          {searchInp?.length > 0 && (
            <button type="reset" aria-label="검색어 지우기" className="p-1">
              <Iconify
                icon={"material-symbols-light:close"}
                className={"!min-w-5 !w-5 !h-5"}
              />
            </button>
          )}
          {isSearchInpInFocus ? (
            <button
              type="button"
              onClick={closeSearchSuggestions}
              className="shrink-0 whitespace-nowrap px-2 py-1 text-[14px] font-semibold text-black/55 dark:text-white/55"
            >
              취소
            </button>
          ) : (
            <button type="submit" aria-label="검색" className="p-1">
              <Iconify icon={"iconamoon:search-thin"} className={""} />
            </button>
          )}
        </div>
        <PlacesSearchSuggestion
          searchInp={searchInp}
          isOpen={isSearchInpInFocus}
          onClose={() => setIsSearchInpInFocus(false)}
          currentUserLocation={currentUserLocation}
          setSearchInp={setSearchInp}
          triggerPlaceSearch={triggerPlaceSearch}
          triggerPlaceDetailsModal={triggerPlaceDetailsModal}
          handleSearchAroundMyLocation={handleSearchAroundMyLocation}
        />
      </form>
    </div>
  );
};

export default PlacesListSearchBar;
